import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent,
} from 'react';
import {
  LANGUAGES,
  VOICE_PROVIDERS,
  type Language,
  type VoiceOption,
  type VoiceProvider,
} from '@voxa/shared';
import { api } from '../lib/api.ts';
import { capabilitiesNow } from '../lib/capabilities.ts';
import { useI18n } from '../i18n/index.tsx';
import { languageOptionLabel } from '../i18n/languageName.ts';

/**
 * One side of the conversation on the creation screen (task 6): the language
 * that side speaks and hears, who reads the translation to them, and in which
 * voice.
 *
 * The voice row is always there and only switches between live and inert, so
 * choosing a provider never changes the height of a panel and never moves the
 * one next to it.
 */

export interface PanelChoice {
  provider: VoiceProvider;
  /** Empty until a voice is picked or the list has arrived. */
  voiceId: string;
  /** The voice's name as listed, so the creator's call screen can say it. */
  name: string;
}

// ---- the voice list, fetched once per language per page load ----------------

type VoiceList =
  { status: 'loading' } | { status: 'ready'; voices: VoiceOption[] } | { status: 'failed' };

const lists = new Map<Language, Promise<VoiceOption[]>>();

function voicesFor(language: Language): Promise<VoiceOption[]> {
  let hit = lists.get(language);
  if (!hit) {
    hit = api.voices(language).then((r) => r.voices);
    // A failure is not remembered: the next language change asks again.
    hit.catch(() => lists.delete(language));
    lists.set(language, hit);
  }
  return hit;
}

function useVoices(language: Language, enabled: boolean): VoiceList {
  const [list, setList] = useState<VoiceList>({ status: 'loading' });
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    setList({ status: 'loading' });
    voicesFor(language).then(
      (voices) => live && setList({ status: 'ready', voices }),
      () => live && setList({ status: 'failed' }),
    );
    return () => {
      live = false;
    };
  }, [language, enabled]);
  return list;
}

// ---- one sample playing at a time, across both panels ----------------------

let nowPlaying: { key: string; audio: HTMLAudioElement } | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function stopSample() {
  nowPlaying?.audio.pause();
  nowPlaying = null;
  notify();
}

function playSample(key: string, url: string) {
  stopSample();
  const audio = new Audio(url);
  nowPlaying = { key, audio };
  audio.addEventListener('ended', () => nowPlaying?.audio === audio && stopSample());
  audio.addEventListener('error', () => nowPlaying?.audio === audio && stopSample());
  void audio.play().catch(() => nowPlaying?.audio === audio && stopSample());
  notify();
}

function usePlaying(key: string): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => nowPlaying?.key === key,
  );
}

function PlayButton({
  sampleKey,
  url,
  disabled,
}: {
  sampleKey: string;
  url: string | null;
  disabled: boolean;
}) {
  const { t } = useI18n();
  const playing = usePlaying(sampleKey);
  // Leaving the screen, or picking another voice, stops what this button started.
  useEffect(() => () => void (nowPlaying?.key === sampleKey && stopSample()), [sampleKey]);
  const label = !url && !disabled ? t.voice.noSample : playing ? t.voice.stop : t.voice.listen;
  return (
    <button
      type="button"
      className={`play${playing ? ' playing' : ''}`}
      disabled={disabled || !url}
      aria-label={label}
      title={label}
      onClick={() => (playing ? stopSample() : url && playSample(sampleKey, url))}
    >
      {playing ? (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <rect x="4" y="4" width="8" height="8" rx="1.5" fill="currentColor" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M5 3.2v9.6l8-4.8z" fill="currentColor" />
        </svg>
      )}
    </button>
  );
}

// ---- the provider list ------------------------------------------------------

/**
 * A list that can say something under each name, which a native <select>
 * cannot. It floats over the panel when open, so opening it moves nothing.
 */
function ProviderPicker({
  labelId,
  value,
  unavailable,
  onChange,
}: {
  labelId: string;
  value: VoiceProvider;
  /** Providers that exist here but cannot serve this language. */
  unavailable: ReadonlySet<VoiceProvider>;
  onChange: (p: VoiceProvider) => void;
}) {
  const { t } = useI18n();
  const buttonId = useId();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);

  const name: Record<VoiceProvider, string> = { openai: 'OpenAI', elevenlabs: 'ElevenLabs' };
  const note: Record<VoiceProvider, string> = {
    openai: t.voice.openaiNote,
    elevenlabs: t.voice.elevenlabsNote,
  };
  const options = VOICE_PROVIDERS.map((id) => ({ id, off: unavailable.has(id) }));
  // Which models are behind each choice, as the server names them. For the
  // second voice that is two: the translation provider still translates.
  const models = (id: VoiceProvider) => capabilitiesNow()?.voice?.models?.[id]?.join(' + ') ?? '';

  const items = () => [
    ...(menu.current?.querySelectorAll<HTMLLIElement>('li:not([aria-disabled="true"])') ?? []),
  ];

  useEffect(() => {
    if (!open) return;
    (menu.current?.querySelector<HTMLLIElement>('li[aria-selected="true"]') ?? items()[0])?.focus();
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const choose = (id: VoiceProvider, off: boolean) => {
    if (off) return;
    onChange(id);
    setOpen(false);
    button.current?.focus();
  };

  const onMenuKey = (e: KeyboardEvent<HTMLUListElement>) => {
    const list = items();
    const i = list.indexOf(document.activeElement as HTMLLIElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      list[(i + 1) % list.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      list[(i - 1 + list.length) % list.length]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const id = (document.activeElement as HTMLElement | null)?.dataset.value as VoiceProvider;
      if (id) choose(id, unavailable.has(id));
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      button.current?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="dd" ref={box}>
      <button
        type="button"
        id={buttonId}
        ref={button}
        className="dd-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${labelId} ${buttonId}`}
        title={models(value) || undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="dd-val">
          <b>{name[value]}</b>
          <Ticker text={note[value]} />
        </span>
        <span className="dd-chev" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <ul
          className="dd-menu"
          role="listbox"
          aria-labelledby={labelId}
          ref={menu}
          onKeyDown={onMenuKey}
        >
          {options.map(({ id, off }) => (
            <li
              key={id}
              role="option"
              tabIndex={-1}
              data-value={id}
              aria-selected={id === value}
              aria-disabled={off || undefined}
              onClick={() => choose(id, off)}
            >
              <b>
                {name[id]}
                {models(id) && <code className="model">{models(id)}</code>}
              </b>
              <span className="n">{off ? t.voice.noVoices : note[id]}</span>
              <span className="tick" aria-hidden="true">
                ✓
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A note that scrolls only when it does not fit, by exactly as much as is
 * hidden. With reduced motion asked for, the stylesheet keeps it still and
 * ends it with an ellipsis instead.
 */
function Ticker({ text }: { text: string }) {
  const box = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => {
      el.classList.remove('run');
      const over = el.scrollWidth - el.clientWidth;
      if (over <= 1) return;
      el.style.setProperty('--shift', `${-over}px`);
      el.style.setProperty('--dur', `${Math.max(4, over / 25 + 3).toFixed(1)}s`);
      el.classList.add('run');
    };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [text]);
  return (
    <span className="dd-note" ref={box}>
      <span className="mq">{text}</span>
    </span>
  );
}

// ---- the panel ---------------------------------------------------------------

export function VoicePanel({
  title,
  languageId,
  languageLabel,
  language,
  onLanguage,
  choice,
  onChoice,
}: {
  title: string;
  /** Kept as `mine` / `theirs`, the ids the creation screen always had. */
  languageId: string;
  languageLabel: string;
  language: Language;
  onLanguage: (l: Language) => void;
  choice: PanelChoice;
  onChoice: (c: PanelChoice) => void;
}) {
  const { t, language: ui } = useI18n();
  const providerLabel = useId();
  const voiceSelect = useId();
  const list = useVoices(language, true);
  const voices = list.status === 'ready' ? list.voices : [];
  const eleven = choice.provider === 'elevenlabs';

  // Keep the choice honest as the list arrives or the language changes: a side
  // set to ElevenLabs always names a voice that exists for its language, and a
  // language with no voices sends the side back to the built-in one.
  useEffect(() => {
    if (!eleven || list.status !== 'ready') return;
    if (voices.length === 0) {
      onChoice({ provider: 'openai', voiceId: '', name: '' });
    } else if (!voices.some((v) => v.id === choice.voiceId)) {
      onChoice({ provider: 'elevenlabs', voiceId: voices[0]!.id, name: voices[0]!.name });
    }
  }, [eleven, list, voices, choice.voiceId, onChoice]);

  const unavailable = new Set<VoiceProvider>(
    list.status === 'ready' && voices.length === 0 ? ['elevenlabs'] : [],
  );
  const picked = voices.find((v) => v.id === choice.voiceId) ?? null;

  let hint: string = t.voice.hintBuiltIn;
  if (eleven) {
    hint =
      list.status === 'loading'
        ? t.voice.loading
        : list.status === 'failed'
          ? t.voice.failed
          : picked && !picked.verified
            ? t.voice.accent
            : t.voice.hintLibrary;
  }

  return (
    <div className="person">
      <h3>{title}</h3>
      <div>
        <label htmlFor={languageId}>{languageLabel}</label>
        <select
          id={languageId}
          value={language}
          onChange={(e) => onLanguage(e.target.value as Language)}
        >
          {LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {languageOptionLabel(l, ui)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label id={providerLabel}>{t.voice.provider}</label>
        <ProviderPicker
          labelId={providerLabel}
          value={choice.provider}
          unavailable={unavailable}
          onChange={(provider) =>
            onChoice({
              provider,
              voiceId: provider === 'elevenlabs' ? (voices[0]?.id ?? '') : '',
              name: provider === 'elevenlabs' ? (voices[0]?.name ?? '') : '',
            })
          }
        />
      </div>
      <div>
        <label htmlFor={voiceSelect}>{t.voice.voice}</label>
        <div className="voice-pick">
          <select
            id={voiceSelect}
            value={eleven ? choice.voiceId : ''}
            disabled={!eleven || voices.length === 0}
            onChange={(e) =>
              onChoice({
                provider: 'elevenlabs',
                voiceId: e.target.value,
                name: voices.find((v) => v.id === e.target.value)?.name ?? '',
              })
            }
          >
            {eleven && voices.length > 0 ? (
              // Checked voices first, then the rest. The server already sorts
              // them that way; the groups only say where the line falls.
              (
                [
                  [t.voice.groupVerified, voices.filter((v) => v.verified)],
                  [t.voice.groupOther, voices.filter((v) => !v.verified)],
                ] as const
              ).map(
                ([label, group]) =>
                  group.length > 0 && (
                    <optgroup key={label} label={label}>
                      {group.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </optgroup>
                  ),
              )
            ) : (
              <option value="">
                {!eleven ? t.voice.builtIn : list.status === 'loading' ? t.voice.loading : '—'}
              </option>
            )}
          </select>
          <PlayButton
            sampleKey={`${languageId}:${picked?.id ?? ''}`}
            url={picked?.previewUrl ?? null}
            disabled={!eleven || !picked}
          />
        </div>
        <div className="voice-sub">
          <p className={`hint${eleven && list.status === 'failed' ? ' warn-text' : ''}`}>{hint}</p>
        </div>
      </div>
    </div>
  );
}
